import * as React from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';

import * as Yup from 'yup';

import { useApplicationContext } from '@common/react/components/Core/Application/Application';
import { BaseInit } from '@common/react/objects/BaseInit';
import { simpleStringValidator } from '@common/react/utils/validationHelpers';
import FormikField from '@common/react/components/Forms/FormikField/FormikField';
import { BaseUser } from '@common/typescript/objects/BaseUser';
import PhoneOrEmailInput, { emailOrPhoneValidator } from '@common/react/components/Forms/PhoneOrEmailInput/PhoneOrEmailInput';
import ServerPageProvider from '@common/react/components/Core/ServerPageProvider/ServerPageProvider';
import { ItemProvider } from '@common/react/components/Core/ItemProvider/ItemProvider';
import { ItemEditor } from '@common/react/components/Core/ItemEditor/ItemEditor';
import { callWithConnectionOnce } from '@common/react/utils/configureSignarR';
import SendCodeButton from '@common/react/components/Pages/Login/SendCodeButton';
import { AuthResult, LoginFormValues, BaseAuthCodeComponentProps } from '@common/react/objects/LoginFormValues';

import '@common/react/scss/components/login.scss';

const validationSchema = Yup.object().shape({
	login: emailOrPhoneValidator,
	password: simpleStringValidator,
});

interface LoginProps<User extends BaseUser, TInit extends BaseInit<User>> {
	containerClassName?: string;
	afterBaseRedirectUser?: (user: User) => React.ReactNode;
	renderAuthWithCode?: (props: BaseAuthCodeComponentProps<User, TInit>) => React.ReactNode;
	logo?: React.ReactNode;
	withFieldTitle?: boolean;
	title?: string;
	withoutServerPage?: boolean;
	request?: string;
	afterSubmit?: (item: LoginFormValues | null, res: AuthResult<User, TInit>) => void;
	onSaveRequestError?: ((error: string) => void);
	buttons?: React.ReactNode;
	afterLogin?: () => void;
	recoverPath?: string;
	authByCodeRequest?: string;
	sendCodeRequest?: string;
}

const Login: <User extends BaseUser, TInit extends BaseInit<User> = BaseInit<User>>(
	props: LoginProps<User, TInit>,
) => React.ReactElement<User> = <User extends BaseUser, TInit extends BaseInit<User> = BaseInit<User>>(
	props: LoginProps<User, TInit>,
) => {
	const search = useLocation().search;

	const [isVisible, setIsVisible] = React.useState<boolean>(false);
	const [openLoginCode, setOpenLoginCode] = React.useState<boolean>(false);
	const [formValues, setFormValues] = React.useState<LoginFormValues | null>(null);

	const { getUser, getHostOptions, handleLogin } = useApplicationContext();
	const user = getUser<User>();
	const hostOptions = getHostOptions();

	const {
		afterBaseRedirectUser,
		renderAuthWithCode,
		withFieldTitle,
		containerClassName = '',
		logo,
		buttons,
		title = 'Login',
		withoutServerPage,
		request = 'auth',
		recoverPath = '/recover',
		afterSubmit = (item, res) => {
			handleLogin(res?.initObject as TInit);

			setFormValues(null);
		},
		onSaveRequestError = (error) => {
			if (error === '2FA enabled' && renderAuthWithCode) {
				setIsVisible(true);

				return true;
			}

			if (error === 'The confirmation code has been sent.') {
				setOpenLoginCode(true);

				return true;
			}
		},
		afterLogin,
		authByCodeRequest = 'authByCode',
		sendCodeRequest,
	} = props;

	const [afterLoginTrigger, setLoginTrigger] = React.useState(false);

	React.useEffect(() => {
		if (user && !afterLoginTrigger) {
			afterLogin && afterLogin();
			setLoginTrigger(true);
		}
	}, [!!user]);

	const redirectUser = React.useCallback((user: User) => {
		if (hostOptions?.redirectUrlName) {
			const redirectUrl = new URLSearchParams(search).get(hostOptions.redirectUrlName);
			if (redirectUrl) {
				return <Navigate to={redirectUrl} />;
			}
		}

		if (afterBaseRedirectUser) {
			return afterBaseRedirectUser(user);
		}

		return <Navigate to="/dashboard" />;
	}, [afterBaseRedirectUser]);

	const beforeSubmit = ({ values, submit }) => {
		setFormValues(values);

		callWithConnectionOnce(() => { submit(); });
	};

	const cancelAuthWithCode = () => {
		setIsVisible(false);
	};

	return (<>
		{!withoutServerPage && <ServerPageProvider path="login" defaultTitle="Login" loader={<></>} />}
		<div className={containerClassName}>
			{logo}
			<div className="enter-page__container">
				<ItemProvider<LoginFormValues, AuthResult<User, TInit>>
					id={-1}
					saveRequest={request}
					add={{
						login: '',
						password: '',
						code: '',
						path: '/',
					}}
					validationSchema={validationSchema}
					onSaveRequestError={onSaveRequestError}
				>
					{renderAuthWithCode && isVisible && !user
						? renderAuthWithCode({ formValues, afterSubmit, cancelAuthWithCode })
						: <div className="enter-page__form general-form">
							{user && redirectUser(user)}
							{title && <div className="site-headline text-center">
								<h1>{title}</h1>
							</div>}
							<ItemEditor<LoginFormValues, AuthResult<User, TInit>>
								withButtons
								successMessage=""
								buttons={buttons}
								saveText="Login"
								beforeSubmit={beforeSubmit}
								afterSubmit={({ item, res }) => afterSubmit(item, res)}
								edit={() => <>
									<FormikField
										fieldName="login"
										title={withFieldTitle ? 'Email or Phone number*' : ''}
										containerClassName="form-group"
										render={(fieldProps, inputProps) => <>
											<PhoneOrEmailInput
												fieldProps={fieldProps}
												className="form-control"
												placeholder={withFieldTitle ? '' : 'Email or Phone number*'}
												{...inputProps}
											/>
											{authByCodeRequest && fieldProps.field.value
												&& <SendCodeButton<User, TInit>
													phone={fieldProps.field.value}
													afterSubmit={(_, res) => afterSubmit(formValues, res)}
													authRequest={authByCodeRequest}
													sendCodeRequest={sendCodeRequest}
													openModal={openLoginCode}
												/>
											}
										</>}
									/>
									<FormikField
										fieldName="password"
										title={withFieldTitle ? 'Password*' : ''}
										inputProps={{ placeholder: withFieldTitle ? '' : 'Password*', type: 'password' }}
										containerClassName="form-group"
									/>
									{recoverPath && <div className="clearfix mb10">
										<div className="pull-right">
											<Link to={recoverPath}>Forget Password?</Link>
										</div>
									</div>}
								</>}
							/>
						</div>
					}
				</ItemProvider>
			</div>
		</div>
	</>
	);
};

export default Login;
